"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import {
  ArrowUp,
  CheckCircle2,
  Copy,
  Grip,
  Layers3,
  Loader2,
  Maximize2,
  Pause,
  PencilLine,
  Play,
  Save,
  Sparkles,
  Trash2,
  Volume2,
  VolumeX,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  approveVideoEditStartFrame,
  cancelVideoEditFragment,
  createVideoEditFragment,
  discardVideoEditSession,
  getOrCreateVideoEditSession,
  getVideoEditContext,
  getVideoEditSession,
  renderVideoEdit,
  saveVideoEditSession,
} from "@/services/ads/videos/videoDraftService";
import type {
  VideoEditClip,
  VideoEditContextResponse,
  VideoEditJobResponse,
  VideoEditSessionResponse,
} from "@/services/ads/videos/videoDraft.types";

type ChatMessage = Record<string, unknown>;
type DragState =
  | { kind: "start" | "end" }
  | { kind: "playhead" }
  | { kind: "range"; anchorTime: number; initialRange: [number, number] };

const MIN_SELECTION_SECONDS = 1;

function textValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function numberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return null;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function formatSeconds(value: number) {
  const safe = Math.max(0, Math.round(value));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function normalizeSelectionRange(range: [number, number], totalDuration: number): [number, number] {
  const max = Math.max(MIN_SELECTION_SECONDS, totalDuration || 30);
  const start = clamp(Math.min(range[0], range[1]), 0, max - MIN_SELECTION_SECONDS);
  const end = clamp(Math.max(range[0], range[1]), start + MIN_SELECTION_SECONDS, max);
  return [Number(start.toFixed(1)), Number(end.toFixed(1))];
}

function clipOverlapSeconds(clip: VideoEditClip, range: [number, number]) {
  return Math.max(0, Math.min(range[1], clip.end_seconds) - Math.max(range[0], clip.start_seconds));
}

function primaryClipForRange(clips: VideoEditClip[], range: [number, number]) {
  if (!clips.length) return null;
  let bestClip = clips[0];
  let bestOverlap = -1;
  clips.forEach((clip) => {
    const overlap = clipOverlapSeconds(clip, range);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      bestClip = clip;
    }
  });
  if (bestOverlap > 0) return bestClip;
  const midpoint = (range[0] + range[1]) / 2;
  return clips.find((clip) => midpoint >= clip.start_seconds && midpoint < clip.end_seconds) || clips[clips.length - 1];
}

function selectionRangeFromJob(job: VideoEditJobResponse | null): [number, number] | null {
  if (!job) return null;
  if (typeof job.start_seconds === "number" && typeof job.end_seconds === "number" && job.end_seconds > job.start_seconds) {
    return [job.start_seconds, job.end_seconds];
  }
  const selection = job.selection;
  if (!selection || typeof selection !== "object") return null;
  const focusRange = (selection as Record<string, unknown>).focus_time_range;
  const rangeSource = focusRange && typeof focusRange === "object" ? focusRange as Record<string, unknown> : selection;
  const start = numberValue((rangeSource as Record<string, unknown>).start_seconds);
  const end = numberValue((rangeSource as Record<string, unknown>).end_seconds);
  if (start == null || end == null || end <= start) return null;
  return [start, end];
}

function instructionForFragment(fragment: VideoEditJobResponse) {
  return textValue(fragment.instruction)
    || textValue([...(fragment.chat_history ?? [])].reverse().find((message) => textValue(message.content))?.content)
    || "Edición solicitada";
}

function rangesOverlap(left: [number, number], right: [number, number]) {
  return left[0] < right[1] && right[0] < left[1];
}

function jobIsPolling(status?: string) {
  return status === "planning" || status === "rendering_segment";
}

function statusLabel(status?: string) {
  switch (status) {
    case "planning":
      return "Preparando";
    case "start_frame_review":
      return "Start frame";
    case "start_frame_approved":
      return "Aprobado";
    case "rendering_segment":
      return "Renderizando";
    case "ready":
      return "Listo";
    case "error":
      return "Error";
    default:
      return "Fragmento";
  }
}

function fragmentTone(status?: string) {
  switch (status) {
    case "planning":
    case "rendering_segment":
      return "border-sky-300 bg-sky-400/70 text-sky-950";
    case "start_frame_review":
    case "start_frame_approved":
      return "border-amber-300 bg-amber-300/80 text-amber-950";
    case "ready":
      return "border-emerald-300 bg-emerald-400/80 text-emerald-950";
    case "error":
      return "border-red-300 bg-red-400/80 text-red-950";
    default:
      return "border-slate-300 bg-slate-400/70 text-slate-950";
  }
}

export function VideoEditorPanel({
  referenceId,
  videoUrl,
  posterUrl,
  onOpenFinished,
}: {
  referenceId: string;
  videoUrl?: string | null;
  posterUrl?: string | null;
  productName?: string | null;
  onOpenFinished?: (targetReferenceId?: string | null) => void;
}) {
  const [context, setContext] = useState<VideoEditContextResponse | null>(null);
  const [session, setSession] = useState<VideoEditSessionResponse | null>(null);
  const [selectedFragmentId, setSelectedFragmentId] = useState<string | null>(null);
  const [selectionRange, setSelectionRange] = useState<[number, number]>([0, 0]);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [mediaDuration, setMediaDuration] = useState<number | null>(null);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [instruction, setInstruction] = useState("");
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [loadingContext, setLoadingContext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const playerShellRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const selectionRangeRef = useRef<[number, number]>([0, 0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    let alive = true;
    const loadEditor = async () => {
      setLoadingContext(true);
      setError(null);
      setContext(null);
      setSession(null);
      setSelectedFragmentId(null);
      setSelectionRange([0, 0]);
      setMediaDuration(null);
      setCurrentVideoTime(0);
      setInstruction("");
      setChatHistory([]);

      try {
        const result = await getVideoEditContext(referenceId);
        if (!alive) return;
        if (!result.res) {
          setError(result.error?.message || "No pudimos cargar el editor del video.");
          return;
        }
        setContext(result.res);
        if (!result.res.can_edit) return;
        if (result.res.active_session) {
          setSession(result.res.active_session);
          setSelectedFragmentId(result.res.active_session.fragments?.[0]?.edit_id ?? null);
          return;
        }

        const sessionResult = await getOrCreateVideoEditSession(referenceId);
        if (!alive) return;
        if (sessionResult.res) setSession(sessionResult.res);
        else setError(sessionResult.error?.message || "No pudimos iniciar la sesión de edición.");
      } catch (err) {
        if (!alive) return;
        console.error("Error loading video editor:", err);
        setError("No pudimos cargar el editor del video.");
      } finally {
        if (alive) setLoadingContext(false);
      }
    };

    void loadEditor();
    return () => {
      alive = false;
    };
  }, [referenceId]);

  const fragments = session?.fragments ?? [];
  const selectedFragment = fragments.find((fragment) => fragment.edit_id === selectedFragmentId)
    || fragments.find((fragment) => fragment.status === "start_frame_review")
    || fragments.find((fragment) => fragment.status === "start_frame_approved")
    || fragments.find((fragment) => jobIsPolling(fragment.status))
    || fragments.find((fragment) => fragment.status === "ready")
    || null;

  useEffect(() => {
    if (!session?.session_id) return;
    const shouldPoll = session.status === "saving" || fragments.some((fragment) => jobIsPolling(fragment.status));
    if (!shouldPoll) return;
    const id = window.setInterval(async () => {
      const result = await getVideoEditSession(session.session_id);
      if (result.res) {
        setSession(result.res);
        if (result.res.status !== "save_error") setError(null);
      }
    }, 3000);
    return () => window.clearInterval(id);
  }, [fragments, session?.session_id, session?.status]);

  const clips = context?.clips ?? [];
  const contextDuration = useMemo(
    () => clips.reduce((sum, clip) => sum + (clip.duration_seconds || 0), 0) || context?.duration || 30,
    [clips, context?.duration]
  );
  const totalDuration = mediaDuration && Number.isFinite(mediaDuration) && mediaDuration > MIN_SELECTION_SECONDS
    ? mediaDuration
    : contextDuration;
  const activeSelectionRange = useMemo(
    () => normalizeSelectionRange(selectionRange, totalDuration),
    [selectionRange, totalDuration]
  );
  const internalTargetClip = useMemo(
    () => primaryClipForRange(clips, activeSelectionRange),
    [clips, activeSelectionRange]
  );
  const selectionDuration = activeSelectionRange[1] - activeSelectionRange[0];
  const selectionLeft = (activeSelectionRange[0] / totalDuration) * 100;
  const selectionWidth = (selectionDuration / totalDuration) * 100;
  const startHandleLeft = (activeSelectionRange[0] / totalDuration) * 100;
  const endHandleLeft = (activeSelectionRange[1] / totalDuration) * 100;
  const playheadLeft = (clamp(currentVideoTime, 0, totalDuration) / totalDuration) * 100;
  const selectionOverlapsFragment = fragments.some((fragment) => {
    const range = selectionRangeFromJob(fragment);
    return Boolean(range && rangesOverlap(activeSelectionRange, range));
  });
  const sessionIsLocked = session?.status === "saving" || session?.status === "saved";
  const canAdjustSelection = Boolean(context?.can_edit && !sessionIsLocked);
  const canSendInstruction = Boolean(
    context?.can_edit
    && session?.session_id
    && internalTargetClip
    && instruction.trim()
    && !submitting
    && !sessionIsLocked
    && !selectionOverlapsFragment
  );
  const readyFragments = fragments.filter((fragment) => fragment.status === "ready");
  const hasPendingFragments = fragments.some((fragment) => ["planning", "start_frame_review", "start_frame_approved", "rendering_segment"].includes(fragment.status));
  const canSave = Boolean(session?.session_id && readyFragments.length > 0 && !hasPendingFragments && session?.status !== "saving");
  const canDiscard = Boolean(session?.session_id && readyFragments.length > 0 && session?.status !== "saving" && session?.status !== "saved");
  const hasRenderedFragments = readyFragments.length > 0;
  const fragmentCards = useMemo(() => {
    const timestamp = (fragment: VideoEditJobResponse, fallback: number) => {
      const raw = fragment.updated_at || fragment.created_at;
      const parsed = raw ? new Date(raw).getTime() : NaN;
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    return fragments
      .map((fragment, index) => ({ fragment, index }))
      .sort((left, right) => timestamp(right.fragment, right.index) - timestamp(left.fragment, left.index))
      .map(({ fragment }) => fragment);
  }, [fragments]);
  const displayVideoUrl = selectedFragment?.status === "ready" && selectedFragment.final_video_url
    ? selectedFragment.final_video_url
    : videoUrl;

  useEffect(() => {
    selectionRangeRef.current = activeSelectionRange;
  }, [activeSelectionRange]);

  useEffect(() => {
    if (!context?.reference_id) return;
    const fallbackDuration = contextDuration || 30;
    setSelectionRange([0, Math.min(10, fallbackDuration)]);
  }, [context?.reference_id, contextDuration]);

  const seekVideo = useCallback((time: number) => {
    const target = clamp(time, 0, totalDuration);
    setCurrentVideoTime(target);
    const video = videoRef.current;
    if (!video || !Number.isFinite(target)) return;
    try {
      video.currentTime = target;
    } catch {
      // Some browsers reject seeks before metadata; state still reflects the intended time.
    }
  }, [totalDuration]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
      return;
    }
    video.pause();
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      setIsMuted((value) => !value);
      return;
    }
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  const expandVideo = useCallback(() => {
    const target = playerShellRef.current || videoRef.current;
    if (!target?.requestFullscreen) return;
    void target.requestFullscreen();
  }, []);

  const pauseForScrub = useCallback(() => {
    const video = videoRef.current;
    if (video && !video.paused) video.pause();
  }, []);

  const setSelectionAndSeek = useCallback((range: [number, number], seekAt?: number) => {
    const next = normalizeSelectionRange(range, totalDuration);
    selectionRangeRef.current = next;
    setSelectionRange(next);
    seekVideo(seekAt ?? next[0]);
  }, [seekVideo, totalDuration]);

  useEffect(() => {
    if (!selectedFragment?.edit_id) return;
    const range = selectionRangeFromJob(selectedFragment);
    if (!range) return;
    const current = selectionRangeRef.current;
    if (Math.abs(current[0] - range[0]) < 0.05 && Math.abs(current[1] - range[1]) < 0.05) return;
    setSelectionAndSeek(range, range[0]);
  }, [
    selectedFragment?.edit_id,
    selectedFragment?.start_seconds,
    selectedFragment?.end_seconds,
    setSelectionAndSeek,
  ]);

  const timeFromClientX = useCallback((clientX: number) => {
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 0;
    return clamp(((clientX - rect.left) / rect.width) * totalDuration, 0, totalDuration);
  }, [totalDuration]);

  const updateSelectionBoundary = useCallback((boundary: "start" | "end", value: number) => {
    const [start, end] = selectionRangeRef.current;
    const boundedValue = clamp(value, 0, totalDuration);
    if (boundary === "start") {
      const next = normalizeSelectionRange([Math.min(boundedValue, end - MIN_SELECTION_SECONDS), end], totalDuration);
      setSelectionAndSeek(next, next[0]);
      return;
    }
    const next = normalizeSelectionRange([start, Math.max(boundedValue, start + MIN_SELECTION_SECONDS)], totalDuration);
    setSelectionAndSeek(next, next[1]);
  }, [setSelectionAndSeek, totalDuration]);

  useEffect(() => {
    if (!dragState) return;
    const handleMove = (event: globalThis.PointerEvent) => {
      event.preventDefault();
      const value = timeFromClientX(event.clientX);
      if (dragState.kind === "range") {
        const duration = dragState.initialRange[1] - dragState.initialRange[0];
        const maxStart = Math.max(0, totalDuration - duration);
        const nextStart = clamp(dragState.initialRange[0] + (value - dragState.anchorTime), 0, maxStart);
        setSelectionAndSeek([nextStart, nextStart + duration], clamp(value, nextStart, nextStart + duration));
        return;
      }
      if (dragState.kind === "playhead") {
        seekVideo(value);
        return;
      }
      updateSelectionBoundary(dragState.kind, value);
    };
    const handleUp = () => setDragState(null);
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [dragState, setSelectionAndSeek, timeFromClientX, totalDuration, updateSelectionBoundary]);

  const handleTimelinePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!clips.length) return;
    pauseForScrub();
    const value = timeFromClientX(event.clientX);
    seekVideo(value);
    setDragState({ kind: "playhead" });
  };

  const beginPlayheadDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!clips.length) return;
    pauseForScrub();
    seekVideo(timeFromClientX(event.clientX));
    setDragState({ kind: "playhead" });
  };

  const beginBoundaryDrag = (boundary: "start" | "end", event: ReactPointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!canAdjustSelection) return;
    pauseForScrub();
    setSelectedFragmentId(null);
    const [start, end] = selectionRangeRef.current;
    seekVideo(boundary === "start" ? start : end);
    setDragState({ kind: boundary });
  };

  const beginRangeDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!canAdjustSelection) return;
    pauseForScrub();
    setSelectedFragmentId(null);
    const range = selectionRangeRef.current;
    const pointerTime = timeFromClientX(event.clientX);
    seekVideo(clamp(pointerTime, range[0], range[1]));
    setDragState({
      kind: "range",
      anchorTime: pointerTime,
      initialRange: range,
    });
  };

  const selectFragment = (fragment: VideoEditJobResponse) => {
    const range = selectionRangeFromJob(fragment);
    setSelectedFragmentId(fragment.edit_id);
    if (range) setSelectionAndSeek(range, range[0]);
  };

  const refreshSession = async () => {
    if (!session?.session_id) return;
    const result = await getVideoEditSession(session.session_id);
    if (result.res) setSession(result.res);
  };

  const submitInstruction = async () => {
    if (!internalTargetClip || !instruction.trim() || !context?.can_edit || !session?.session_id || selectionOverlapsFragment) return;
    setSubmitting(true);
    setError(null);
    const nextHistory = [...chatHistory, { role: "user", content: instruction.trim() }];
    const selection = {
      target_clip_key: internalTargetClip.clip_key,
      target_clip_index: internalTargetClip.clip_index,
      start_seconds: activeSelectionRange[0],
      end_seconds: activeSelectionRange[1],
      focus_time_range: {
        start_seconds: activeSelectionRange[0],
        end_seconds: activeSelectionRange[1],
      },
    };
    const result = await createVideoEditFragment(session.session_id, {
      selection,
      instruction: instruction.trim(),
      chat_history: nextHistory,
    });
    if (result.res) {
      setSelectedFragmentId(result.res.edit_id);
      setChatHistory(nextHistory);
      setInstruction("");
      await refreshSession();
    } else {
      setError(result.error?.message || "No pudimos crear la edición.");
    }
    setSubmitting(false);
  };

  const approveStartFrame = async (fragment: VideoEditJobResponse | null = selectedFragment) => {
    if (!fragment?.edit_id) return;
    setSelectedFragmentId(fragment.edit_id);
    setActionLoading(true);
    setError(null);
    const result = await approveVideoEditStartFrame(fragment.edit_id, fragment.start_frame_url);
    if (result.res) {
      setSelectedFragmentId(result.res.edit_id);
      await refreshSession();
    } else {
      setError(result.error?.message || "No pudimos aprobar el start frame.");
    }
    setActionLoading(false);
  };

  const renderApprovedEdit = async (fragment: VideoEditJobResponse | null = selectedFragment) => {
    if (!fragment?.edit_id) return;
    setSelectedFragmentId(fragment.edit_id);
    setActionLoading(true);
    setError(null);
    const result = await renderVideoEdit(fragment.edit_id);
    if (result.res) {
      setSelectedFragmentId(result.res.edit_id);
      await refreshSession();
    } else {
      setError(result.error?.message || "No pudimos renderizar la edición.");
    }
    setActionLoading(false);
  };

  const cancelSelectedFragment = async (fragment: VideoEditJobResponse | null = selectedFragment) => {
    if (!fragment?.edit_id) return;
    setSelectedFragmentId(fragment.edit_id);
    setActionLoading(true);
    setError(null);
    const result = await cancelVideoEditFragment(fragment.edit_id);
    if (result.res) {
      setSelectedFragmentId(null);
      await refreshSession();
    } else {
      setError(result.error?.message || "No pudimos eliminar ese fragmento.");
    }
    setActionLoading(false);
  };

  const saveSession = async (mode: "replace" | "copy") => {
    if (!session?.session_id) return;
    setActionLoading(true);
    setError(null);
    const result = await saveVideoEditSession(session.session_id, mode);
    if (result.res) setSession(result.res);
    else setError(result.error?.message || "No pudimos guardar la edición.");
    setActionLoading(false);
  };

  const discardSession = async () => {
    if (!session?.session_id) return;
    setActionLoading(true);
    setError(null);
    const result = await discardVideoEditSession(session.session_id);
    if (result.res) {
      setSession(result.res);
      setSelectedFragmentId(null);
    } else {
      setError(result.error?.message || "No pudimos descartar los cambios.");
    }
    setActionLoading(false);
  };

  if (loadingContext) {
    return (
      <div className="min-h-0 flex-1 overflow-hidden bg-[#F6F7FB]">
        <div className="grid h-full min-h-0 gap-5 p-5 xl:grid-cols-[minmax(380px,0.48fr)_minmax(440px,0.52fr)]">
          <section className="flex min-h-0 min-w-0 items-center justify-center">
            <div className="flex h-full w-full max-w-[430px] flex-col justify-center">
              <div className="mx-auto aspect-[9/16] h-[min(70vh,720px)] max-h-[calc(96vh_-_230px)] max-w-full animate-pulse overflow-hidden rounded-[24px] border border-slate-200 bg-gradient-to-br from-slate-200 via-white to-slate-100" />
              <div className="mt-4 rounded-[24px] border border-slate-200/80 bg-white/95 p-3 shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="flex gap-1.5">
                    <div className="h-9 w-9 animate-pulse rounded-full bg-slate-200" />
                    <div className="h-9 w-9 animate-pulse rounded-full bg-slate-100" />
                  </div>
                  <div className="h-3 w-20 animate-pulse rounded-full bg-slate-200" />
                  <div className="h-9 w-9 animate-pulse rounded-full bg-slate-100" />
                </div>
                <div className="mb-2 flex justify-between">
                  <div className="h-3 w-24 animate-pulse rounded-full bg-slate-200" />
                  <div className="h-3 w-10 animate-pulse rounded-full bg-slate-200" />
                </div>
                <div className="h-24 animate-pulse rounded-[18px] bg-slate-100" />
              </div>
            </div>
          </section>
          <main className="flex min-h-0 min-w-0 flex-col justify-center">
            <div className="mx-auto flex w-full max-w-[460px] flex-col items-center text-center">
              <div className="mb-6 h-[104px] w-[104px] animate-pulse rounded-full bg-slate-200" />
              <div className="h-8 w-72 animate-pulse rounded-full bg-slate-200" />
              <div className="mt-3 h-4 w-56 animate-pulse rounded-full bg-slate-100" />
            </div>
            <div className="mt-8 rounded-[28px] border border-gray-100 bg-white px-4 pb-3 pt-3 shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
              <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
              <div className="mt-3 flex items-center justify-between">
                <div className="h-8 w-28 animate-pulse rounded-full bg-slate-100" />
                <div className="h-9 w-9 animate-pulse rounded-full bg-slate-200" />
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-hidden bg-[#F6F7FB]">
      <div className="grid h-full min-h-0 gap-5 p-5 xl:grid-cols-[minmax(380px,0.48fr)_minmax(440px,0.52fr)]">
        <section className="flex min-h-0 min-w-0 items-center justify-center">
          <div className="flex h-full w-full max-w-[430px] flex-col justify-center">
            <div
              ref={playerShellRef}
              className="mx-auto aspect-[9/16] max-w-full overflow-hidden rounded-[24px] border border-black/10 bg-black shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
              style={{ height: "min(70vh, 720px)", maxHeight: "calc(96vh - 230px)" }}
            >
              {displayVideoUrl ? (
                <video
                  key={displayVideoUrl}
                  ref={videoRef}
                  src={displayVideoUrl}
                  poster={posterUrl || undefined}
                  muted={isMuted}
                  playsInline
                  onLoadedMetadata={(event) => {
                    const duration = event.currentTarget.duration;
                    if (Number.isFinite(duration) && duration > MIN_SELECTION_SECONDS) {
                      setMediaDuration(duration);
                    }
                    setCurrentVideoTime(event.currentTarget.currentTime || 0);
                    setIsMuted(event.currentTarget.muted);
                  }}
                  onTimeUpdate={(event) => setCurrentVideoTime(event.currentTarget.currentTime || 0)}
                  onSeeked={(event) => setCurrentVideoTime(event.currentTarget.currentTime || 0)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onVolumeChange={(event) => setIsMuted(event.currentTarget.muted)}
                  className="h-full w-full object-contain"
                />
              ) : (
                <div className="flex aspect-[9/16] items-center justify-center bg-slate-950 text-slate-500">
                  <Play className="h-8 w-8" />
                </div>
              )}
            </div>

            <div className="mt-4 rounded-[24px] border border-slate-200/80 bg-white/95 p-3 shadow-[0_18px_45px_rgba(15,23,42,0.08)] backdrop-blur">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={togglePlay}
                    disabled={!displayVideoUrl}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-950 text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label={isPlaying ? "Pausar" : "Reproducir"}
                    title={isPlaying ? "Pausar" : "Reproducir"}
                  >
                    {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-px" />}
                  </button>
                  <button
                    type="button"
                    onClick={toggleMute}
                    disabled={!displayVideoUrl}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label={isMuted ? "Activar audio" : "Silenciar"}
                    title={isMuted ? "Activar audio" : "Silenciar"}
                  >
                    {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </button>
                </div>
                <span className="text-xs font-semibold tabular-nums text-slate-500">
                  {formatSeconds(currentVideoTime)} / {formatSeconds(totalDuration)}
                </span>
                <button
                  type="button"
                  onClick={expandVideo}
                  disabled={!displayVideoUrl}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Expandir video"
                  title="Expandir video"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>

              <div className="mb-2 flex items-center justify-between gap-3 text-xs">
                <span className="font-semibold text-slate-800">
                  {formatSeconds(activeSelectionRange[0])} - {formatSeconds(activeSelectionRange[1])}
                </span>
                <span className={cn("font-semibold", selectionOverlapsFragment ? "text-amber-600" : "text-slate-500")}>
                  {selectionOverlapsFragment ? "Zona ocupada" : `${selectionDuration.toFixed(1)}s`}
                </span>
              </div>

              <div
                onPointerDown={handleTimelinePointerDown}
                className="relative h-24 select-none overflow-hidden rounded-[18px] border border-slate-200 bg-slate-100 cursor-col-resize"
              >
                <div
                  ref={timelineRef}
                  data-video-editor-timeline="true"
                  className="absolute inset-x-3 inset-y-0 overflow-visible"
                >
                  <div className="absolute inset-x-0 top-9 h-8 overflow-hidden rounded-full bg-slate-200">
                    {clips.length > 0 ? clips.map((clip, index) => {
                      const left = `${(clip.start_seconds / totalDuration) * 100}%`;
                      const width = `${Math.max(0, (clip.duration_seconds / totalDuration) * 100)}%`;
                      return (
                        <div
                          key={`${clip.clip_key}-${index}`}
                          aria-hidden="true"
                          className={cn(
                            "absolute inset-y-0 border-r border-white/70",
                            index % 2 === 0 ? "bg-slate-200" : "bg-slate-300/70"
                          )}
                          style={{ left, width }}
                        />
                      );
                    }) : (
                      <div className="flex h-full items-center justify-center text-xs text-slate-500">Sin segmentos</div>
                    )}
                  </div>

                  {fragments.map((fragment) => {
                    const range = selectionRangeFromJob(fragment);
                    if (!range) return null;
                    const left = (range[0] / totalDuration) * 100;
                    const width = ((range[1] - range[0]) / totalDuration) * 100;
                    const isSelected = selectedFragment?.edit_id === fragment.edit_id;
                    const compactStatus = jobIsPolling(fragment.status);
                    return (
                      <button
                        key={fragment.edit_id}
                        type="button"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={() => selectFragment(fragment)}
                        className={cn(
                          "absolute top-6 z-30 flex h-12 min-w-[28px] items-center justify-center overflow-hidden rounded-full border px-1.5 text-[10px] font-bold shadow-sm transition",
                          fragmentTone(fragment.status),
                          isSelected && "ring-2 ring-slate-950/70"
                        )}
                        style={{ left: `${left}%`, width: `${Math.max(width, 3)}%` }}
                        title={`${statusLabel(fragment.status)} ${formatSeconds(range[0])} - ${formatSeconds(range[1])}`}
                      >
                        {compactStatus ? (
                          <PencilLine className="h-3.5 w-3.5 animate-pulse" />
                        ) : (
                          <span className="block truncate">{statusLabel(fragment.status)}</span>
                        )}
                      </button>
                    );
                  })}

                  {clips.length > 0 && (
                    <>
                      <button
                        type="button"
                        data-video-editor-playhead="true"
                        aria-label="Mover cursor del video"
                        onPointerDown={beginPlayheadDrag}
                        className="absolute top-2 z-[60] flex h-20 w-5 -translate-x-1/2 items-center justify-center rounded-full text-slate-950 transition hover:bg-white/60"
                        style={{ left: `${playheadLeft}%` }}
                      >
                        <span className="h-20 w-0.5 rounded-full bg-slate-950 shadow-[0_0_0_1px_rgba(255,255,255,0.7)]" />
                      </button>
                      <div
                        className={cn(
                          "pointer-events-none absolute top-4 z-10 h-[68px] rounded-[14px] border-2 shadow-[0_0_0_999px_rgba(15,23,42,0.08)]",
                          selectionOverlapsFragment ? "border-amber-400 bg-amber-200/30" : "border-primary bg-primary/20"
                        )}
                        style={{ left: `${selectionLeft}%`, width: `${selectionWidth}%` }}
                      />
                      <button
                        type="button"
                        aria-label="Mover selección"
                        onPointerDown={beginRangeDrag}
                        disabled={!canAdjustSelection}
                        className={cn(
                          "group absolute top-4 z-40 flex h-[68px] items-center justify-center rounded-[14px] text-primary transition",
                          canAdjustSelection ? "cursor-grab active:cursor-grabbing hover:bg-primary/10" : "cursor-default"
                        )}
                        style={{ left: `${selectionLeft}%`, width: `${selectionWidth}%` }}
                      >
                        <Grip className="h-4 w-4 rounded-full bg-white/95 p-0.5 opacity-0 shadow-sm transition group-hover:opacity-100" />
                      </button>
                      <button
                        type="button"
                        aria-label="Inicio de la selección"
                        onPointerDown={(event) => beginBoundaryDrag("start", event)}
                        disabled={!canAdjustSelection}
                        className="absolute top-3 z-[80] flex h-[76px] w-4 -translate-x-1/2 items-center justify-center rounded-full border border-primary bg-white text-primary shadow-sm transition hover:bg-primary hover:text-white disabled:cursor-default disabled:opacity-70"
                        style={{ left: `${startHandleLeft}%` }}
                      >
                        <span className="h-7 w-0.5 rounded-full bg-current" />
                      </button>
                      <button
                        type="button"
                        aria-label="Fin de la selección"
                        onPointerDown={(event) => beginBoundaryDrag("end", event)}
                        disabled={!canAdjustSelection}
                        className="absolute top-3 z-[80] flex h-[76px] w-4 -translate-x-1/2 items-center justify-center rounded-full border border-primary bg-white text-primary shadow-sm transition hover:bg-primary hover:text-white disabled:cursor-default disabled:opacity-70"
                        style={{ left: `${endHandleLeft}%` }}
                      >
                        <span className="h-7 w-0.5 rounded-full bg-current" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <main className="flex min-h-0 min-w-0 flex-col">
          {context && !context.can_edit && (
            <div className="mb-3 rounded-[18px] border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
              {context.fallback_reason || "Este video no tiene artifacts suficientes para editarse todavía."}
            </div>
          )}

          {error && (
            <div className="mb-3 rounded-[18px] border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
          )}

          <div className={cn(
            "flex min-h-0 flex-1 flex-col transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
            fragmentCards.length ? "justify-between" : "justify-center"
          )}>
            {!fragmentCards.length && (
              <div className="mb-8 flex flex-col items-center text-center transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]">
                <img
                  src="/fluxi-ai-orb.png"
                  alt=""
                  className="mb-6 h-[104px] w-[104px] object-contain opacity-[0.78]"
                />
                <h3 className="mx-auto max-w-[330px] pb-1 text-[28px] font-semibold leading-[1.2] text-gray-900">
                  ¿Qué quieres cambiar de este <span className="text-primary">fragmento</span> del video?
                </h3>
              </div>
            )}

            {fragmentCards.length > 0 && (
              <div className="mb-4 min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                {fragmentCards.map((fragment) => {
                  const range = selectionRangeFromJob(fragment);
                  const isSelected = selectedFragment?.edit_id === fragment.edit_id;
                  const isBusyFragment = jobIsPolling(fragment.status);
                  return (
                    <section
                      key={fragment.edit_id}
                      onClick={() => selectFragment(fragment)}
                      className={cn(
                        "cursor-pointer rounded-[20px] border bg-white p-4 text-left shadow-[0_10px_28px_rgba(15,23,42,0.08)] transition hover:border-primary/25",
                        isSelected ? "border-primary/35 ring-2 ring-primary/10" : "border-gray-100"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[12px] font-semibold uppercase tracking-wide text-[#5050EA]">
                            {range ? `${formatSeconds(range[0])} - ${formatSeconds(range[1])}` : "Fragmento"}
                          </p>
                          <p className="mt-1 text-sm leading-relaxed text-gray-900">
                            {instructionForFragment(fragment)}
                          </p>
                        </div>
                        <span className={cn(
                          "inline-flex h-8 shrink-0 items-center justify-center rounded-full px-3 text-xs font-semibold",
                          fragment.status === "error"
                            ? "bg-red-100 text-red-700"
                            : fragment.status === "ready"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-[#5050EA]/10 text-[#5050EA]"
                        )}>
                          {isBusyFragment ? (
                            <PencilLine className="h-3.5 w-3.5 animate-pulse" />
                          ) : (
                            statusLabel(fragment.status)
                          )}
                        </span>
                      </div>

                      {fragment.status === "start_frame_review" && (
                        <div className="mt-4 grid gap-4 sm:grid-cols-[150px_1fr]">
                          <div className="overflow-hidden rounded-[16px] border border-slate-200 bg-slate-100">
                            {fragment.start_frame_url && (
                              <img src={fragment.start_frame_url} alt="" className="h-56 w-full object-cover" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <h5 className="text-sm font-semibold text-slate-950">Aprueba el inicio</h5>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">
                              Esta imagen será el primer frame del fragmento nuevo.
                            </p>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                approveStartFrame(fragment);
                              }}
                              disabled={actionLoading}
                              className="mt-4 inline-flex h-10 items-center gap-2 rounded-full bg-[#5050EA] px-4 text-sm font-semibold text-white transition hover:bg-[#4040D8] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {actionLoading && selectedFragment?.edit_id === fragment.edit_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                              Aprobar
                            </button>
                          </div>
                        </div>
                      )}

                      {fragment.status === "start_frame_approved" && (
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[16px] border border-emerald-200 bg-emerald-50 p-3">
                          <div>
                            <h5 className="text-sm font-semibold text-emerald-950">Start frame aprobado</h5>
                            <p className="text-xs text-emerald-700">Ya puedes renderizar este fragmento.</p>
                          </div>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              renderApprovedEdit(fragment);
                            }}
                            disabled={actionLoading}
                            className="inline-flex h-10 items-center gap-2 rounded-full bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {actionLoading && selectedFragment?.edit_id === fragment.edit_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            Renderizar
                          </button>
                        </div>
                      )}

                      {fragment.status === "ready" && fragment.final_video_url && (
                        <div className="mt-4 rounded-[16px] border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                          Fragmento listo en el timeline.
                        </div>
                      )}

                      {isBusyFragment && (
                        <div className="mt-4 flex items-center gap-2 rounded-[16px] border border-sky-200 bg-sky-50 p-3 text-sm font-medium text-sky-800">
                          <PencilLine className="h-4 w-4 animate-pulse" />
                          Procesando el fragmento
                        </div>
                      )}

                      {fragment.status === "error" && (
                        <div className="mt-4 rounded-[16px] border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                          {fragment.error_message || "Este fragmento falló. Puedes eliminarlo y volver a intentarlo."}
                        </div>
                      )}

                      {!isBusyFragment && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            cancelSelectedFragment(fragment);
                          }}
                          disabled={actionLoading}
                          className="mt-4 inline-flex h-9 items-center gap-2 rounded-full border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Eliminar
                        </button>
                      )}
                    </section>
                  );
                })}
              </div>
            )}

            <section className={cn(
              "rounded-[28px] border border-gray-100 bg-white px-4 pb-3 pt-3 shadow-[0_2px_12px_rgba(0,0,0,0.08)] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
              !fragmentCards.length && instruction.trim() && "translate-y-5 shadow-[0_18px_45px_rgba(15,23,42,0.10)]"
            )}>
              {selectionOverlapsFragment && (
                <p className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs font-semibold text-amber-800">
                  Esa zona ya tiene una edición. Elige otro rango del timeline.
                </p>
              )}

              <textarea
                value={instruction}
                onChange={(event) => setInstruction(event.target.value)}
                rows={3}
                disabled={!context?.can_edit || submitting || sessionIsLocked}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    if (canSendInstruction) void submitInstruction();
                  }
                }}
                className="w-full resize-none bg-transparent px-0 py-1 text-sm leading-relaxed text-gray-900 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
                placeholder="Ej: haz este fragmento más natural, con la modelo aplicándose el producto frente al espejo."
              />

              <div className="mt-2 flex items-center justify-between">
                <span className="inline-flex h-8 items-center gap-2 rounded-full bg-slate-100 px-3 text-xs font-semibold text-slate-500">
                  <Layers3 className="h-3.5 w-3.5" />
                  {readyFragments.length}/{fragments.length} listos
                </span>
                <button
                  type="button"
                  onClick={submitInstruction}
                  disabled={!canSendInstruction}
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-all",
                    submitting || instruction.trim()
                      ? "bg-[#5050EA] text-white hover:bg-[#4040D8]"
                      : "cursor-not-allowed bg-gray-100 text-gray-400"
                  )}
                  title="Crear fragmento"
                  aria-label="Crear fragmento"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
                </button>
              </div>
            </section>

            {hasRenderedFragments && (
            <section className="mt-3 flex flex-col items-end gap-3">
              <div className="flex flex-wrap items-center justify-end gap-2">
                {session?.status === "saving" && <Loader2 className="mr-1 h-4 w-4 animate-spin text-primary" />}
                <button
                  type="button"
                  onClick={discardSession}
                  disabled={!canDiscard || actionLoading}
                  className="inline-flex h-10 w-fit items-center gap-2 rounded-full border border-slate-200 px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" />
                  Descartar
                </button>
                <button
                  type="button"
                  onClick={() => saveSession("copy")}
                  disabled={!canSave || actionLoading}
                  className="inline-flex h-10 w-fit items-center gap-2 rounded-full bg-[#5050EA] px-4 text-sm font-semibold text-white transition hover:bg-[#4040D8] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Copy className="h-4 w-4" />
                  Guardar como copia
                </button>
                <button
                  type="button"
                  onClick={() => saveSession("replace")}
                  disabled={!canSave || actionLoading}
                  className="inline-flex h-10 w-fit items-center gap-2 rounded-full bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Save className="h-4 w-4" />
                  Guardar
                </button>
              </div>

              {session?.status === "saved" && (
                <div className="mt-3 rounded-[16px] border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-sm font-semibold text-emerald-950">Video guardado</p>
                  {session.saved_video_reference_id && (
                    <button
                      type="button"
                      onClick={() => onOpenFinished?.(session.saved_video_reference_id)}
                      className="mt-3 inline-flex h-9 items-center gap-2 rounded-full bg-emerald-600 px-3 text-xs font-semibold text-white transition hover:bg-emerald-700"
                    >
                      <Play className="h-3.5 w-3.5" />
                      Ver video
                    </button>
                  )}
                </div>
              )}
            </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
